import * as React from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";

import {
  Button,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  withStyles,
} from "@material-ui/core";
import Alert from '@material-ui/lab/Alert';
import { Email, Phone } from "@material-ui/icons";

import SectionHeader from "../../components/UI/SectionHeader";
import { getSorting, stableSort } from "../../utilities/sorting";
import { formatDatetime, formatPhone } from "../../utilities/format";
import { isMobile } from "../../utilities/mobile";
import { isBasicView, isPremiumView } from "../../utilities/access";

import { appAction, clientAction, householdAction, portfolioAction, redtailAction, userAction } from "../../actions";

import { ImportClientModal, MetricsCard, RemoveHouseholdModal } from "./components";

const styles = (theme) => ({
  table: {
    [theme.breakpoints.down("md")]: {
      width: "110%",
    },
    "& th": {
      color: theme.palette.primary.main,
      fontSize: 14,
      padding: "4px 10px",
      fontWeight: 500,
      height: "48px",
    },
    "& td": {
      fontSize: 14,
      padding: "4px 10px",
      [theme.breakpoints.down("md")]: {
        padding: "2px",
        fontSize: 12,
      },
    },
  },
  contact: {
    "& svg": {
      fontSize: 16,
      marginRight: "2%",
      verticalAlign: "middle",
      color: "rgba(0, 0, 0, 0.54)",
    },
  },
  clientActions: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    "& button": {
      marginLeft: theme.spacing(2),
    }
  },
  actionBtns: {
    "& button": {
      minWidth: 98,
      [theme.breakpoints.down("md")]: {
        whiteSpace: "nowrap",
        minWidth: 87,
        maxWidth: 87,
        minHeight: 24,
        lineHeight: 0.8,
      },
    },
    "& button:last-child": {
      marginLeft: theme.spacing(1),
    },
  },
  actionBtn: {
    fontSize: 12,
    textTransform: "none",
    textAlign: "center",
  },
  metricStat: {
    fontSize: 36,
    fontWeight: 500,
  },
  pendingTable: {
    overflow: "auto",
    width: "100%",
    marginTop: 12,
    "& td": {
      fontSize: 14,
    },
  },
  message: {
    fontSize: theme.typography.fontSize,
  },
});

class Dashboard extends React.Component {
  state = {
    isPhone: isMobile.phone(),
    archivingPortfolioId: "",
    order: "desc",
    orderBy: "",
    columns: [
      {
        id: "name",
        numeric: false,
        sortable: true,
        disablePadding: true,
        label: "Household",
      },
      {
        id: "tableView.onboarded",
        numeric: false,
        sortable: true,
        disablePadding: true,
        label: "# Onboarded",
        extraOrdering: [["asc", "tableView.clientName"]],
      },
      {
        id: "tableView.clientName",
        numeric: false,
        sortable: true,
        disablePadding: true,
        label: "Client",
      },
      {
        id: "tableView.spouseName",
        numeric: false,
        sortable: true,
        disablePadding: true,
        label: "Spouse",
      },
      {
        id: "tableView.clientEmail",
        numeric: false,
        sortable: true,
        disablePadding: true,
        label: "Contact",
      },
      {
        id: "updatedAt",
        numeric: false,
        sortable: true,
        disablePadding: true,
        label: "Last Modified",
      },
      { id: "action", numeric: false, label: "" },
    ],
    isImportClientModalOpen: false,
  };

  archiveClient = () => {
    // TODO: archive household
    // let { archivingPortfolioId } = this.state;
    // if (archivingPortfolioId)
    //   this.props.dispatch(portfolioAction.archiveClient(archivingPortfolioId))
  };

  handleRequestSort = (event, property, extraOrdering) => {
    const orderBy = property;
    let order = "desc";
    if (this.state.orderBy === property && this.state.order === "desc") {
      order = "asc";
    }
    this.setState({ order, orderBy, extraOrdering });
  };

  /* Redirect to the Household screen */
  redirectToHousehold = (householdId) => {
    this.props.history.push(`/Portal/household/${householdId}`);
  };

  /* Redirect to the Loss Tolerance screen */
  redirectToRisk = (householdId) => {
    this.props.history.push(`/Portal/household/${householdId}/risk`);
  };

  /* Redirect to the Profile screen */
  redirectToProfile = (householdId) => {
    this.props.history.push(`/Portal/household/${householdId}/client`);
  };

  /*
    Redirect to household or risk screen
    by clicking the table row
  */
  tableRowClick = (household) => {
    const { id, risk, rror } = household;
    if (risk === 0) {
      if(rror === 0) {
        this.redirectToProfile(id);
        return;
      }
      this.redirectToRisk(id);
      return;
    }
    this.redirectToHousehold(id);
  };

  handleImportClientModalOpen = () => {
    this.setState({ isImportClientModalOpen: true });
  };

  handleImportClientModalClose = () => {
    this.setState({ isImportClientModalOpen: false });
    // Clear Redtail message on exiting modal
    if (!this.props.redtail.runningJob) {
      this.props.dispatch(redtailAction.clearMessage());
    }
  };

  componentDidMount() {
    this.props.dispatch(portfolioAction.clearCurrentPortfolio());
    this.props.dispatch(householdAction.clearCurrentHousehold());
    this.props.dispatch(appAction.setPortfolioNav());
    this.props.dispatch(appAction.loadUiConfigs());
    this.props.dispatch(userAction.loadUserApplicationState());
    if (this.state.isPhone) {
      this.props.dispatch(appAction.toggleMainNav(false));
    }
    window.scrollTo(0, 0);

    // Check running Redtail job status
    if( this.props.app.redtailRunningJob && this.props.app.redtailRunningJob.status === "in-progress" ) {
      this.props.dispatch(redtailAction.lastImportJob());
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Update state if removeHouseholdById is called
    if (prevProps.household.isRequesting && this.props.household.isSuccess) {
      this.props.dispatch(userAction.loadUserApplicationState());
    }

    // Update households once Redtail job is completed
    if ( prevProps.app.redtailRunningJob
      && prevProps.app.redtailRunningJob.status === "in-progress"
      && this.props.app.redtailRunningJob.status === "completed" ) {
      this.props.dispatch(clientAction.getClients());
    }
  }

  componentWillUnmount() {
    this.props.dispatch(householdAction.clearMessage());
  }

  render() {
    const { app, accessLevel, clients, household, redtail, classes } = this.props;
    const { order, orderBy, extraOrdering } = this.state;
    const { allClients } = clients;
    const { requestSuccess, pending, runningJob, message } = redtail;
    const allOnboardedPortfolios = allClients.reduce(
      (acc, currentValue) =>
        acc.concat(
          currentValue.Portfolios.filter(
            (p) => p.onboardStatus === "onboarded" && p.onboarded
          )
        ),
      []
    );

    const ActionButton = ({ household }) => {
      const { risk, rror, isSample } = household;

      const action = {
        COMPLETED: {
          CTA: "View Household",
          COLOR: "primary",
        },
        INCOMPLETE: {
          CTA: "Loss Simulator",
          COLOR: "secondary",
        },
        IMPORTED: {
          CTA: "Finish Profile",
          COLOR: "primary",
        },
        SAMPLE: {
          CTA: "View Portfolios",
          COLOR: "primary",
        },
      }

      function getAction() {
        if (isSample) return action.SAMPLE;
        if (risk === 0) {
          if (rror === 0) {
            return action.IMPORTED;
          }
          return action.INCOMPLETE;
        }
        return action.COMPLETED;
      }

      return (
        <React.Fragment>
          <Button 
            variant="outlined"
            size="small"
            className={classes.actionBtn}
            color={getAction().COLOR}
            onClick={() => this.tableRowClick(household)}
            data-cy="dashboard.household.action"
          >
            { getAction().CTA }
          </Button>
          {!isSample && (
            <RemoveHouseholdModal
              household={household}
              className={classes.actionBtn}
            />
          )}
        </React.Fragment>
      )
    }

    const PendingPaperworkPortfoliosTable = ({ households }) => {
      return (
        <section className={classes.pendingTable}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Household Name</TableCell>
                <TableCell>Portfolio Name</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {households.map((household) =>
                household.Portfolios.filter(
                  (p) => p.onboardStatus === "forms-generated"
                ).map((portfolio) => {
                  return (
                    <TableRow key={portfolio.uuid}>
                      <TableCell>{household.name}</TableCell>
                      <TableCell>{portfolio.name}</TableCell>
                      <TableCell>
                        <Link
                          to={`/Portal/household/${household.id}/summary/${portfolio.uuid}`}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            className={classes.actionBtn}
                          >
                            View Summary
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </section>
      );
    };

    return (
      <React.Fragment>
        <SectionHeader text={"Dashboard"} />
        {isPremiumView(accessLevel) && (
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MetricsCard
                    cardTitle={"# of Clients"}
                    paperStyle={{ height: 200 }}
                    cardContent={
                      <Typography className={classes.metricStat}>
                        {clients.allClients.length}
                      </Typography>
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <MetricsCard
                    cardTitle={"# of Onboarded Portfolios"}
                    paperStyle={{ height: 200 }}
                    cardContent={
                      <Typography className={classes.metricStat}>
                        {allOnboardedPortfolios.length}
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <MetricsCard
                cardTitle={"Pending portfolios paperwork"}
                cardContent={
                  <PendingPaperworkPortfoliosTable households={allClients} />
                }
                paperStyle={{
                  height: 424,
                  overflow: "auto",
                  justifyContent: "start",
                }}
              />
            </Grid>
          </Grid>
        )}
        {!isBasicView(accessLevel) && (
          <Grid
            container
            direction="row"
            justify="flex-end"
            alignItems="center"
            className={classes.clientActions}
          >
            {app.redtailEnabled &&
              <React.Fragment>
                <ImportClientModal
                  isOpen={this.state.isImportClientModalOpen}
                  onClose={this.handleImportClientModalClose}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={this.handleImportClientModalOpen}
                >
                  {pending || runningJob ? (
                    <React.Fragment>
                      <CircularProgress size={16} style={{marginRight: 8}} />
                      Importing Contacts
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      Import from Redtail
                    </React.Fragment>
                  )}
                </Button>
              </React.Fragment>
            }
            <Link to={"/Portal/start"}>
              <Button
                variant="contained"
                color="primary"
              >
                Add Client
              </Button>
            </Link>
          </Grid>
        )}
        { household.responseMessage && (
          <Alert
            severity={household.isSuccess ? "success" : "error"}
            className={classes.message}
          >
            { household.responseMessage }
          </Alert>
        )}
        { message && (
          <Alert
            severity={requestSuccess ? "success" : "error"}
            icon={pending && <CircularProgress size={16} />}
            className={classes.message}
          >
            { message }
          </Alert>
        )}
        <section>
          <Table className={classes.table} aria-labelledby="tableTitle">
            <TableHead>{this.renderTableHeadRow()}</TableHead>
            <TableBody>
              {stableSort(
                clients.allClients,
                getSorting(order, orderBy, extraOrdering)
              ).map((household, i) => {
                return (
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={`household-${i}`}
                  >
                    <TableCell>{household.name}</TableCell>
                    {!isBasicView(accessLevel) && (
                      <React.Fragment>
                        <TableCell className={classes.onboardCell}>
                          {household.tableView.onboarded}
                        </TableCell>
                        <TableCell>{household.tableView.clientName}</TableCell>
                        <TableCell>{household.tableView.spouseName}</TableCell>
                        <TableCell>
                          <div className={classes.contact}>
                            {household.tableView.clientEmail && (
                              <React.Fragment>
                                <Email />
                                {household.tableView.clientEmail}
                              </React.Fragment>
                            )}
                          </div>
                          <div className={classes.contact}>
                            {household.tableView.clientPhone && (
                              <React.Fragment>
                                <Phone />
                                {formatPhone(household.tableView.clientPhone)}
                              </React.Fragment>
                            )}
                          </div>
                        </TableCell>
                      </React.Fragment>
                    )}
                    <TableCell>{formatDatetime(household.updatedAt)}</TableCell>
                    <TableCell className={classes.actionBtns} align={"right"}>
                      <ActionButton household={household} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </section>
      </React.Fragment>
    );
  }

  renderTableHeadRow() {
    const { order, orderBy, columns } = this.state;
    const { accessLevel, classes } = this.props;

    let createSortHandler = (property, extraOrdering) => (event) => {
      this.handleRequestSort(event, property, extraOrdering);
    };

    const HouseholdRow = ({ data }) => (
      <TableRow>
        {data.map(({ id, numeric, disablePadding, sortable, extraOrdering, label }) => (
          <TableCell
            key={id}
            align={numeric ? "right" : "left"}
            padding={disablePadding ? "none" : "default"}
            sortDirection={orderBy === id ? order : false}
            className={classes.onboardCell}
          >
            {sortable ? (
              <Tooltip
                title="Sort"
                placement={numeric ? "bottom-end" : "bottom-start"}
                enterDelay={300}
              >
                <TableSortLabel
                  active={orderBy === id}
                  direction={order}
                  onClick={createSortHandler(id, extraOrdering)}
                >
                  {label}
                </TableSortLabel>
              </Tooltip>
            ) : (
              label
            )}
          </TableCell>
        ))}
      </TableRow>
    )

    if (isBasicView(accessLevel)) {
      return <HouseholdRow data={columns.filter(({id}) => !id.includes("tableView"))} />
    }

    return <HouseholdRow data={columns} />
  }
}

const mapStateToProps = (state) => {
  return {
    app: state.app,
    accessLevel: state.user.user.userRole && state.user.user.userRole.accessLevel ? state.user.user.userRole.accessLevel : 0,
    clients: state.client,
    household: state.household,
    redtail: state.redtail,
  };
};
export default withStyles(styles)(connect(mapStateToProps)(Dashboard));



// WEBPACK FOOTER //
// ./src/containers/Dashboard/index.jsx